import clsx from "classnames";
import { Container } from "react-bootstrap";
import HighlightMedia from "./components/HighlightMedia";
import HighlightContent from "./components/HighlightContent";
import HighlightButtons from "./components/HighlightButtons";
import classes from "./styles.module.scss";

/**
 * Highlight box: a rounded, shadowed card split into an image and a text panel, with the image on
 * either side and optional buttons inside the card or underneath it. Stacks image-first on mobile.
 *
 * @param {string} [className] - Additional classes for the root element (spacing).
 * @param {string} [bgColor] - Background colour class for the section.
 * @param {string} [cardBgColor] - Background colour class for the text panel.
 * @param {string} [imageSide] - "left" or "right".
 * @param {string} [imageWidth] - Share of the card taken by the image: "40", "45" or "50".
 * @param {Object} [image] - CMS image.
 * @param {string} [imageAlt] - Alt text override.
 * @param {string} [imagePosition] - Focal point for the cropped image.
 * @param {string} [title] - Heading text.
 * @param {string} [titleColor] - Text colour class for the heading.
 * @param {string} [description] - Rich text HTML.
 * @param {string} [textColor] - Text colour class for the copy.
 * @param {string} [verticalAlign] - "start" or "center" alignment of the text panel.
 * @param {string} [buttonPlacement] - "below" the card or "inside" the text panel.
 * @param {Array} [button] - Button items.
 * @returns {JSX.Element}
 */
export const HighlightBox = ({
  className = "py-5",
  bgColor = "",
  cardBgColor = "bg-light",
  imageSide = "left",
  imageWidth = "45",
  image,
  imageAlt,
  imagePosition = "center center",
  title,
  titleColor = "text-primary",
  description,
  textColor = "text-body",
  verticalAlign = "center",
  buttonPlacement = "below",
  button,
}) => {
  const buttons = Array.isArray(button) ? button : button ? [button] : [];
  const buttonsInside = buttonPlacement === "inside";
  const hasImage = Boolean(image?.url);

  return (
    <div data-component="HighlightBox" className={clsx(className, bgColor, classes.highlightBox)}>
      <Container className="mw-xxl">
        <div
          className={clsx(classes.highlightBox__card, {
            [classes["highlightBox__card--reverse"]]: imageSide === "right",
            [classes["highlightBox__card--noImage"]]: !hasImage,
            [classes["highlightBox__card--alignStart"]]: verticalAlign === "start",
          })}
          style={{ "--hb-img-width": `${imageWidth}%` }}
        >
          <HighlightMedia image={image} imageAlt={imageAlt} imagePosition={imagePosition} />

          <div className={clsx(classes.highlightBox__panel, cardBgColor)}>
            <HighlightContent
              title={title}
              titleColor={titleColor}
              description={description}
              textColor={textColor}
              buttons={buttonsInside ? buttons : []}
            />
          </div>
        </div>

        {!buttonsInside && <HighlightButtons buttons={buttons} className={classes["highlightBox__buttons--below"]} />}
      </Container>
    </div>
  );
};

export default HighlightBox;
